<template>
  <div class="overflow-y-hidden w-full h-[100lvh] bg-cw-green-dark flex flex-col fixed left-0 top-0 z-50">
    <div class="px-40 py-20 heightMd:pt-8 heightSm:pt-8 grid grid-cols-[2fr_4fr_2fr] gap-3">
      <aside class="flex flex-col items-center justify-center h-full">
        <div class="mt-8" v-if="hasAcceptedOffer">
          <!-- <BaseButton @click="cancelTrade" backgroundColor="var(--color-cw-red-light)" borderColor="var(--color-cw-red-border-shadow)">
            <p class="w-full heightSm:!text-sm !text-base xl:!text-xl">Cancel trade</p>
          </BaseButton> -->
          <TradeSwapCards :cardToTrade :letter="tradeLetter" :currentTypeCard="currentTypeCard" />
        </div>
      </aside>
      <main class="flex flex-col items-center justify-center h-full">
        <div class="relative min-w-96 max-w-max bg-cw-dark rounded-[16px] px-10 pt-10 pb-12 w-full">
          <div class="mt-1">
            <div class="flex flex-col items-center" v-if="hasAcceptedOffer">
              <h1 class="text-2xl font-bold text-white mb-10">Choose a trade option</h1>
              <div class="">
                <div class="grid grid-cols-[40px_300px_auto] gap-4 mb-8" v-for="(offer, index) in allOffers" :key="offer.playerId">
                  <BaseAvatar :size="40" :src="offer.player?.avatarUrl" />
                  <div>
                    <h4 class="text-white font-semibold">{{ offer.player?.username }}</h4>
                    <h5 class="text-cw-grey-2">{{ offer.status == 'accepted' ? 'agreed to trade with you' : 'rejected your trade' }}</h5>
                  </div>

                  <BaseButton @click="acceptTrade(index, offer.playerId)" :disabled="offer.status == 'rejected'" background-color="var(--color-cw-green)" border-color="var(--color-cw-green-border-shadow)">
                    <p class="w-full heightSm:!text-sm !text-base xl:!text-xl">Accept</p>
                  </BaseButton>
                </div>
              </div>
            </div>
            <div class="flex flex-col items-center" v-else>
              <SearchIcon class="h-auto w-20 mb-5" />
              <h1 class="text-2xl font-bold text-white mb-10">{{ allOffers.length > 0 ? 'Nobody accepted your trade request' : 'No one has your card' }}</h1>
              <div class="mb-10">
                <BaseButton @click="cancelTrade('random')" backgroundColor="var(--color-cw-lilac-alt)" borderColor="var(--color-cw-lilac-border-shadow)">
                  <p class="w-full heightSm:!text-sm !text-base xl:!text-xl px-12">Get a random card</p>
                </BaseButton>
              </div>
              <!-- <div class="mb-10">
                <BaseButton @click="cancelTrade('restart')" backgroundColor="var(--color-cw-olive)" borderColor="var(--color-cw-olive-border-shadow)">
                  <p class="w-full heightSm:!text-sm !text-base xl:!text-xl px-8">Trade a different card</p>
                </BaseButton>
              </div> -->

              <!-- <a href="" @click.prevent="cancelTrade('home')" class="inline-flex items-center mt-2 text-xl font-semibold text-cw-green-lighter">Back to game</a> -->
            </div>
          </div>
        </div>
        <div class="absolute -z-1 inset-0 bg-cw-dark-alt/85 flex flex-col items-center justify-end"></div>
      </main>
      <GameTimer
        :timeRemaining="userPlayerProfile.actionTimeout"
        :key="userPlayerProfile.actionTimeout"
        v-if="userPlayerProfile && userPlayerProfile.actionTimeout > 0"
        class="bg-cw-green-border-shadow absolute bottom-10 left-10"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import TradeSwapCards from '@/components/game-play/TradeSwapCards.vue'
import { dummyCards } from '@/data/onboarding'
import { useGameArea } from '@/composables/useGameArea'
import { useHelpers } from '@/composables/useHelpers'
import useSocket from '@/composables/useSocket'
import { GameEvent } from '@/types/Events'
import type { CardCategoryT, CardT } from '@/types/Game'
import { useGameStore } from '@/stores/game'
import { storeToRefs } from 'pinia'
import SearchIcon from '@/assets/svgs/icons/search-icon.svg'
import { useQueue } from '@/composables/useQueue'
import GameTimer from '@/components/interface/GameTimer.vue'

const showLoader = inject('showLoader') as InjectedFunctionT
const hideLoader = inject('hideLoader') as InjectedFunctionT

const { gameState, userPlayerProfile } = storeToRefs(useGameStore())
const { setActiveGameAreaScreen, tradeOfferPlayerId, setActiveFinalisingTradeScreen, canCloseTradeCardOptions } = useGameArea()
const { handleCustomError } = useHelpers()
const { getCardById, getPlayerById } = useGameStore()
const socket = useSocket()
const { addToQueue } = useQueue()

const tradeCardType = computed(() => {
  return gameState.value!.activeTrade!.targetCategory as CardCategoryT
})

const tradeLetter = computed(() => {
  return gameState.value!.activeTrade!.targetLetter
})

const hasAcceptedOffer = computed(() => {
  return gameState.value!.activeTrade!.offers.some(offer => offer.status === 'accepted')
})

const allOffers = computed(() => {
  return gameState.value!.activeTrade!.offers.map(offer => {
    return {
      ...offer,
      player: getPlayerById(offer.playerId),
      card: getCardById(offer.cardId)
    }
  })
})

const cardToTrade = computed(() => {
  return getCardById(gameState.value!.activeTrade!.swapCardId)
})

const currentTypeCard = computed(() => {
  switch (tradeCardType.value) {
    case 'plan':
      return dummyCards.find(card => card.category === 'plan') as CardT
    case 'save':
      return dummyCards.find(card => card.category === 'save') as CardT
    case 'invest':
      return dummyCards.find(card => card.category === 'invest') as CardT
    default:
      return dummyCards.find(card => card.category === 'plan') as CardT
  }
})

const cancelTrade = async (action: 'random' | 'restart' | 'home') => {
  try {
    showLoader()
    await socket.sendEventAsync<CardT[]>(GameEvent.CancelTrade)
    if (action === 'random') {
      canCloseTradeCardOptions.value = false
      addToQueue('tradeRandomCard', () => setActiveGameAreaScreen('RANDOM_CARD_EXCHANGE_SELECTION_SCREEN'))
    }
    //  else if (action === 'restart') {
    //   addToQueue('tradeIdleScreen', () => setActiveGameAreaScreen('TRADE_CARD_SELECTION_SCREEN'))
    // }
  } catch (error: any) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

const acceptTrade = async (offerIndex: number, offerPlayerID: string) => {
  try {
    tradeOfferPlayerId.value = offerPlayerID
    showLoader()
    await socket.sendEventAsync<CardT[]>(GameEvent.SelectOffer, {
      offerIndex
    })
    setActiveFinalisingTradeScreen('TRADE_MCQ_SCREEN')
  } catch (error) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}
</script>
